// src/pages/VacationRequests/HrAdminVacationRequestsPage.jsx

import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Link,
  TablePagination,
  Stack,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import HistoryIcon from '@mui/icons-material/History';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import dayjs from 'dayjs';
import { buildPublicFileUrl } from '../../config/env';

function StatusChip({ value }) {
  const v = (value || 'Pending').toLowerCase();
  const map = {
    approved: { color: 'success', icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> },
    rejected: { color: 'error', icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} /> },
    cancelled: { color: 'warning', icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} /> },
    pending: { color: 'info', icon: <HourglassBottomIcon sx={{ fontSize: 16 }} /> },
  };
  const { color, icon } = map[v] || { color: 'default', icon: <HistoryIcon sx={{ fontSize: 16 }} /> };
  return <Chip size="small" color={color} variant="outlined" icon={icon} label={value || 'Pending'} />;
}

function HrStatusChip({ value }) {
  const v = (value || 'Pending').toLowerCase();
  const map = {
    approved: { color: 'success', icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> },
    rejected: { color: 'error', icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} /> },
    pending: { color: 'default', icon: <HourglassBottomIcon sx={{ fontSize: 16 }} /> },
  };
  const { color, icon } = map[v] || map.pending;
  return <Chip size="small" color={color} variant="outlined" icon={icon} label={value || 'Pending'} />;
}

function DetailRow({ label, children }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}
    >
      <Typography variant="body2" sx={{ width: 140, color: 'text.secondary', flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  );
}

const fmtDate = (d) => (d ? dayjs(d).format('MMM D, YYYY') : '—');

// Team-filter helpers: the employee's department (eager-loaded department_name)
// and their reporting-line manager (eager-loaded user.manager.name).
const deptOf = (req) => (req?.user?.department_name || '').trim();
const managerOf = (req) => (req?.user?.manager?.name || '').trim();

const getVacationRequestDisplayStatus = (request = {}) => {
  const status = String(request?.status || '').toLowerCase();
  const hrStatus = String(request?.hr_status || '').toLowerCase();

  if (['rejected', 'cancelled'].includes(status)) return status;
  if (['approved', 'rejected', 'cancelled'].includes(hrStatus)) return hrStatus;
  if (status === 'approved' && hrStatus === 'pending') return 'pending';
  return status || 'pending';
};

function HrAdminVacationRequestsPage() {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [requestToProcess, setRequestToProcess] = useState(null);
  const [actionToTake, setActionToTake] = useState(null);
  const [processing, setProcessing] = useState(false);

  const [queueMode, setQueueMode] = useState('pending');
  const [query, setQuery] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');
  const [managerFilter, setManagerFilter] = useState('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [errorMsg, setErrorMsg] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [viewRequest, setViewRequest] = useState(null);

  useEffect(() => {
    fetchRequests(queueMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueMode]);

  const fetchRequests = async (mode = queueMode) => {
    try {
      setLoading(true);
      const res = await api.get('/admin/vacation-requests2/index', {
        params: {
          view_mode: 'assigned_requests',
          queue: mode,
          paginate: false,
        },
      });
      setRequests(res.data || []);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
      setErrorMsg('Failed to load vacation requests.');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = (request, action) => {
    setRequestToProcess(request);
    setActionToTake(action);
    setConfirmDialogOpen(true);
  };

  const confirmAction = async () => {
    if (!requestToProcess || !actionToTake) return;
    try {
      setProcessing(true);
      await api.patch(`/admin/vacation-requests/${requestToProcess.id}`, {
        hr_status: actionToTake,
      });
      setOkMsg(`Request ${actionToTake.toLowerCase()} successfully.`);
      setConfirmDialogOpen(false);
      setRequestToProcess(null);
      setActionToTake(null);
      await fetchRequests();
    } catch (err) {
      console.error('Failed to process request:', err);
      setErrorMsg('Failed to process the request.');
    } finally {
      setProcessing(false);
    }
  };

  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setRequestToProcess(null);
    setActionToTake(null);
  };

  // Distinct team (department) and manager option lists, built from the loaded
  // assigned-requests so HR can narrow the list to a single team / manager.
  const deptOptions = useMemo(() => {
    const set = new Set();
    for (const r of requests || []) {
      const d = deptOf(r);
      if (d) set.add(d);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [requests]);

  const managerOptions = useMemo(() => {
    const set = new Set();
    for (const r of requests || []) {
      const m = managerOf(r);
      if (m) set.add(m);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [requests]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (requests || []).filter((r) => {
      if (q) {
        const name = r?.user?.name?.toLowerCase() || '';
        const leave = r?.leave_type?.toLowerCase() || '';
        if (!name.includes(q) && !leave.includes(q)) return false;
      }
      if (deptFilter !== 'All' && deptOf(r) !== deptFilter) return false;
      if (managerFilter !== 'All' && managerOf(r) !== managerFilter) return false;
      return true;
    });
  }, [requests, query, deptFilter, managerFilter]);

  // Reset to the first page whenever the result set is re-scoped.
  useEffect(() => {
    setPage(0);
  }, [query, deptFilter, managerFilter, queueMode]);

  // Keep the current page within range after the list shrinks (e.g. after an
  // approval removes a row from the pending queue). Functional update avoids
  // re-running on `page` changes and never loops.
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
    setPage((p) => (p > maxPage ? maxPage : p));
  }, [filtered.length, rowsPerPage]);

  const paginated = useMemo(() => {
    const startIdx = page * rowsPerPage;
    return filtered.slice(startIdx, startIdx + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10) || 10);
    setPage(0);
  };

  const stats = useMemo(() => {
    const base = { total: filtered.length, pending: 0, approved: 0, rejected: 0, cancelled: 0 };
    for (const r of filtered) {
      const s = getVacationRequestDisplayStatus(r);
      if (base[s] !== undefined) base[s] += 1;
      else if (s.includes('pend')) base.pending += 1;
    }
    return base;
  }, [filtered]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <CircularProgress />
        <Typography sx={{ mt: 1 }} color="text.secondary">
          Loading vacation requests…
        </Typography>
      </Box>
    );
  }

  const headerGrad = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${
    theme.palette.secondary2?.main || '#2389ba'
  } 100%)`;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : theme.palette.background.paper,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: headerGrad,
          color: '#fff',
          py: { xs: 3, md: 4 },
          px: { xs: 2, md: 4 },
          borderRadius: 2,
          mb: 3,
          boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
        }}
      >
        <Typography variant={downSm ? 'h5' : 'h4'} sx={{ mb: 1.5, fontWeight: 700 }}>
          {queueMode === 'archive' ? 'HR Admin Vacation History' : 'HR Admin Vacation Requests'}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
            label={`Total: ${stats.total}`}
            sx={{ color: 'inherit', borderColor: alpha('#fff', 0.4) }}
            variant="outlined"
          />
          <Chip
            size="small"
            color="info"
            icon={<HourglassBottomIcon sx={{ fontSize: 16 }} />}
            label={`Pending: ${stats.pending}`}
            variant="filled"
          />
          <Chip
            size="small"
            color="success"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            label={`Approved: ${stats.approved}`}
            variant="filled"
          />
          <Chip
            size="small"
            color="error"
            icon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
            label={`Rejected: ${stats.rejected}`}
            variant="filled"
          />
          <Chip
            size="small"
            color="warning"
            icon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
            label={`Cancelled: ${stats.cancelled}`}
            variant="filled"
          />
        </Stack>
      </Box>

      {/* Toolbar */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{
          mb: 2,
          p: 1.25,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          background:
            theme.palette.mode === 'dark'
              ? alpha('#fff', 0.03)
              : alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1 }}>
          <SearchIcon sx={{ opacity: 0.6 }} />
          <TextField
            size="small"
            fullWidth
            placeholder="Search by employee or leave type…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            select
            size="small"
            label="Team"
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="All">All Teams</MenuItem>
            {deptOptions.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Manager"
            value={managerFilter}
            onChange={(e) => setManagerFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="All">All Managers</MenuItem>
            {managerOptions.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>

          {(deptFilter !== 'All' || managerFilter !== 'All') && (
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                setDeptFilter('All');
                setManagerFilter('All');
              }}
            >
              Clear
            </Button>
          )}

          <ToggleButtonGroup
            exclusive
            size="small"
            value={queueMode}
            onChange={(_, value) => value && setQueueMode(value)}
            aria-label="Vacation request queue"
            sx={{ alignSelf: 'center' }}
          >
            <ToggleButton value="pending" aria-label="Pending vacation requests">
              Pending Action
            </ToggleButton>
            <ToggleButton value="archive" aria-label="Vacation request history">
              History
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={() => fetchRequests()} color="primary">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Table */}
      {filtered.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, md: 5 },
            textAlign: 'center',
            borderRadius: 3,
            background:
              theme.palette.mode === 'dark'
                ? alpha('#fff', 0.02)
                : alpha(theme.palette.primary.light, 0.06),
          }}
        >
          <HistoryIcon sx={{ fontSize: 40, opacity: 0.6 }} />
          <Typography sx={{ mt: 1 }} variant="h6">
            {queueMode === 'archive' ? 'No vacation history found' : 'No pending vacation requests found'}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Try adjusting your search or refresh the list.
          </Typography>
          <Button startIcon={<RefreshIcon />} onClick={() => fetchRequests()} variant="contained">
            Refresh
          </Button>
        </Paper>
      ) : (
        <TableContainer
          sx={{
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            maxHeight: 560,
          }}
        >
          <Table stickyHeader size="small" aria-label="HR admin vacation requests table">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? alpha('#fff', 0.03)
                        : alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontWeight: 700,
                  },
                }}
              >
                <TableCell>Employee</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Leave Type</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>HR Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginated.map((req) => {
                const start = req.start_date ? dayjs(req.start_date) : null;
                const end = req.end_date ? dayjs(req.end_date) : null;
                const duration =
                  start && end ? Math.max(1, end.diff(start, 'day') + 1) : '—';
                const canAct =
                  queueMode === 'pending' &&
                  String(req.status || '').toLowerCase() === 'approved' &&
                  String(req.hr_status || '').toLowerCase() === 'pending';
                const mgr = managerOf(req);

                return (
                  <TableRow
                    key={req.id}
                    hover
                    sx={{
                      transition: 'background .15s ease',
                      '&:nth-of-type(odd)': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? alpha('#fff', 0.01)
                            : alpha(theme.palette.primary.light, 0.02),
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {req.user?.name || 'Unknown'}
                      </Typography>
                      {mgr && (
                        <Typography variant="caption" color="text.secondary">
                          Mgr: {mgr}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{deptOf(req) || '—'}</TableCell>
                    <TableCell>{req.leave_type || '—'}</TableCell>
                    <TableCell>{fmtDate(req.start_date)}</TableCell>
                    <TableCell>{fmtDate(req.end_date)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={typeof duration === 'number' ? `${duration} day${duration > 1 ? 's' : ''}` : '—'}
                      />
                    </TableCell>
                    <TableCell><StatusChip value={req.status} /></TableCell>
                    <TableCell><HrStatusChip value={req.hr_status} /></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setViewRequest(req)}
                            aria-label="View vacation details"
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canAct && (
                          <>
                            <Tooltip title="Approve">
                              <span>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  disabled={processing}
                                  onClick={() => handleApproval(req, 'Approved')}
                                  startIcon={<CheckCircleOutlineIcon />}
                                >
                                  Approve
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <span>
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  disabled={processing}
                                  onClick={() => handleApproval(req, 'Rejected')}
                                  startIcon={<CancelOutlinedIcon />}
                                >
                                  Reject
                                </Button>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {filtered.length > 0 && (
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 1 }}
        />
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={processing ? undefined : closeConfirmDialog} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            Are you sure you want to{' '}
            <strong>{actionToTake}</strong>{' '}
            this vacation request?
          </Typography>
          {requestToProcess && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                background:
                  theme.palette.mode === 'dark'
                    ? alpha('#fff', 0.02)
                    : alpha(theme.palette.warning.light, 0.08),
              }}
            >
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  <strong>Employee:</strong> {requestToProcess.user?.name || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  <strong>Leave:</strong> {requestToProcess.leave_type || '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>From:</strong> {fmtDate(requestToProcess.start_date)}{' '}
                  <strong>To:</strong> {fmtDate(requestToProcess.end_date)}
                </Typography>
              </Stack>
            </Paper>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirmDialog} color="primary" disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={confirmAction}
            color={actionToTake === 'Approved' ? 'success' : 'error'}
            variant="contained"
            disabled={processing}
            startIcon={actionToTake === 'Approved' ? <CheckCircleOutlineIcon /> : <CancelOutlinedIcon />}
          >
            {processing ? 'Processing…' : `Yes, ${actionToTake}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={!!viewRequest} onClose={() => setViewRequest(null)} fullWidth maxWidth="sm">
        <DialogTitle>Vacation Request Details</DialogTitle>
        <DialogContent dividers>
          {viewRequest && (
            <Stack>
              <DetailRow label="Employee">
                <Typography variant="body2" fontWeight={600}>
                  {viewRequest.user?.name || 'Unknown'}
                  {viewRequest.user?.employee_code ? ` (#${viewRequest.user.employee_code})` : ''}
                </Typography>
              </DetailRow>
              {viewRequest.user?.department_name && (
                <DetailRow label="Department">
                  <Typography variant="body2">{viewRequest.user.department_name}</Typography>
                </DetailRow>
              )}
              {viewRequest.user?.manager?.name && (
                <DetailRow label="Manager">
                  <Typography variant="body2">{viewRequest.user.manager.name}</Typography>
                </DetailRow>
              )}
              <DetailRow label="Leave Type">
                <Typography variant="body2">{viewRequest.leave_type || '—'}</Typography>
              </DetailRow>
              <DetailRow label="From">
                <Typography variant="body2">{fmtDate(viewRequest.start_date)}</Typography>
              </DetailRow>
              <DetailRow label="To">
                <Typography variant="body2">{fmtDate(viewRequest.end_date)}</Typography>
              </DetailRow>
              <DetailRow label="Duration">
                <Typography variant="body2">
                  {(() => {
                    const s = viewRequest.start_date ? dayjs(viewRequest.start_date) : null;
                    const e = viewRequest.end_date ? dayjs(viewRequest.end_date) : null;
                    const d = s && e ? Math.max(1, e.diff(s, 'day') + 1) : null;
                    return d ? `${d} day${d > 1 ? 's' : ''}` : '—';
                  })()}
                </Typography>
              </DetailRow>
              <DetailRow label="Status">
                <StatusChip value={viewRequest.status} />
              </DetailRow>
              <DetailRow label="HR Status">
                <HrStatusChip value={viewRequest.hr_status} />
              </DetailRow>
              {(viewRequest.reason ||
                viewRequest.reason_for_leave ||
                viewRequest.note ||
                viewRequest.notes) && (
                <DetailRow label="Reason">
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewRequest.reason ||
                      viewRequest.reason_for_leave ||
                      viewRequest.note ||
                      viewRequest.notes}
                  </Typography>
                </DetailRow>
              )}
              {(viewRequest.rejection_reason ||
                viewRequest.hr_comment ||
                viewRequest.manager_comment) && (
                <DetailRow label="Note">
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewRequest.rejection_reason ||
                      viewRequest.hr_comment ||
                      viewRequest.manager_comment}
                  </Typography>
                </DetailRow>
              )}
              {viewRequest.created_at && (
                <DetailRow label="Requested on">
                  <Typography variant="body2">{fmtDate(viewRequest.created_at)}</Typography>
                </DetailRow>
              )}
              {Array.isArray(viewRequest.attachments) && viewRequest.attachments.length > 0 && (
                <DetailRow label="Attachments">
                  <Stack spacing={0.5}>
                    {viewRequest.attachments.map((att, idx) => (
                      <Link
                        key={`${att?.id ?? idx}-${att?.file_path ?? 'file'}`}
                        href={buildPublicFileUrl(att?.url || att?.file_path) || '#'}
                        target="_blank"
                        rel="noopener"
                        underline="hover"
                        variant="body2"
                      >
                        Attachment {idx + 1}
                      </Link>
                    ))}
                  </Stack>
                </DetailRow>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewRequest(null)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!okMsg}
        autoHideDuration={3000}
        onClose={() => setOkMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setOkMsg('')} variant="filled">
          {okMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={4000}
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg('')} variant="filled">
          {errorMsg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default HrAdminVacationRequestsPage;
